/**
 * Advance payment — an amount the admin asks the customer to pay before the
 * final bill. The final checkout answers 400 ("Pay the advance of ₹X first")
 * until it is captured, so this surfaces at route level on every stage and
 * inside the payment gate overlay.
 *
 * Flow mirrors the final payment: the pay trigger calls
 * `POST /payments/{id}/advance/initiate` (creates the Razorpay order), opens
 * Razorpay with the returned key/order, then hands the signed triple to
 * `POST /payments/advance/verify`. On capture the backend issues a receipt
 * voucher, which then shows up in the request's bills.
 */
import * as React from 'react';
import { Check, Eye, Loader2, Wallet } from 'lucide-react';
import { Button, toast } from '@finmin/ui';
import { isApiError } from '@finmin/api-client';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import {
  isAdvanceDue,
  useAdvance,
  useInitiateAdvance,
  useVerifyAdvance,
  type AdvanceResponse,
} from '@/api/payments';
import { useMe } from '@/api/auth';
import { getServiceMeta } from '@/features/dashboard/constants';
import { viewBillPdf } from '@/features/billing/open-bill-pdf';
import { useRazorpay, type RazorpaySuccess } from '@/features/payment/use-razorpay';

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

function formatInr(value: string | number | null | undefined): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? inr.format(n) : '—';
}

/**
 * `pay()` runs the whole advance Razorpay flow. `busy` is true from the
 * initiate call until verification settles (or the customer dismisses
 * Razorpay). Only mount it while an advance is due: it warms checkout.js.
 */
function useAdvancePayment(request: CustomerServiceRequestResponse, advance: AdvanceResponse) {
  const serviceTitle = getServiceMeta(request.service_type).title;
  const { openCheckout } = useRazorpay();
  const { data: me } = useMe();
  const initiate = useInitiateAdvance();
  const verify = useVerifyAdvance();
  const [checkoutBusy, setCheckoutBusy] = React.useState(false);
  const busy = checkoutBusy || verify.isPending;

  function handleVerify(resp: RazorpaySuccess) {
    verify.mutate(
      {
        serviceRequestId: request.id,
        payload: {
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        },
      },
      {
        onSuccess: (data) => {
          setCheckoutBusy(false);
          if (data.status === 'captured') {
            toast.success('Advance paid', 'Your receipt is in the bills for this service.');
          } else {
            // Not captured: the payment can't count as the advance (the booking no longer
            // needs it), so the backend records it for a refund.
            toast.error("This payment couldn't be counted as the advance. If you were charged, it will be refunded.");
          }
        },
        onError: () => {
          setCheckoutBusy(false);
          toast.error('We could not verify your payment. If money was deducted, it will be reconciled shortly.');
        },
      },
    );
  }

  async function pay() {
    if (busy) return;
    setCheckoutBusy(true);
    try {
      const order = await initiate.mutateAsync(request.id);
      // Never charge more than the button showed: if the admin changed the advance
      // since the page loaded, show the new amount and wait for a second click.
      if (order.amount_paise !== Math.round(Number(advance.amount) * 100)) {
        setCheckoutBusy(false);
        toast.warning(`Advance updated to ${formatInr(order.amount)}. Review and pay.`);
        return;
      }
      await openCheckout({
        key: order.razorpay_key_id,
        order_id: order.razorpay_order_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'FinminOS',
        description: `Advance for ${serviceTitle}`,
        prefill: {
          name: me?.name ?? undefined,
          email: me?.email ?? undefined,
          contact: me?.mobile_e164 ?? undefined,
        },
        handler: handleVerify,
        modal: { ondismiss: () => setCheckoutBusy(false) },
      });
    } catch (err) {
      setCheckoutBusy(false);
      toast.error(isApiError(err) ? err.message : 'Could not start payment. Please try again.');
    }
  }

  return { busy, pay };
}

/** "Pay advance ₹X" — opens Razorpay for the advance and verifies it. */
export function AdvancePayButton({
  request,
  advance,
  disabled = false,
  size,
  className,
}: {
  request: CustomerServiceRequestResponse;
  advance: AdvanceResponse;
  disabled?: boolean;
  size?: 'sm';
  className?: string;
}) {
  const { busy, pay } = useAdvancePayment(request, advance);
  return (
    <Button type="button" size={size} className={className} onClick={() => void pay()} disabled={busy || disabled}>
      {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
      {busy ? 'Processing…' : `Pay advance ${formatInr(advance.amount)}`}
    </Button>
  );
}

/** "Pay advance ₹X" while due; "Advance paid" with its receipt once captured. */
export function AdvancePaymentCard({
  request,
  readOnly = false,
  className,
}: {
  request: CustomerServiceRequestResponse;
  readOnly?: boolean;
  className?: string;
}) {
  const { data: advance } = useAdvance(request.id);
  if (!advance) return null;
  const due = isAdvanceDue(advance);
  if (!due && advance.status !== 'captured') return null;
  // While the route's <PaymentGateOverlay> is up (active gate, not on hold /
  // closed) it carries its own advance pay button — hide this one so only one
  // pay button, with one busy state, is actionable.
  if (due && request.payment_required && !readOnly) return null;

  return (
    <div className={className}>
      <div className="rounded-2xl border border-border bg-background p-4 shadow-sm">
        {due ? (
          <DueAdvance request={request} advance={advance} disabled={readOnly} />
        ) : (
          <PaidAdvance advance={advance} />
        )}
      </div>
    </div>
  );
}

function DueAdvance({
  request,
  advance,
  disabled,
}: {
  request: CustomerServiceRequestResponse;
  advance: AdvanceResponse;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Wallet className="h-5 w-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Advance payment due</p>
          <p className="text-xs text-muted-foreground">
            {advance.status === 'failed'
              ? 'Your last attempt did not go through. Please try again.'
              : `Pay ${formatInr(advance.amount)} now. It is deducted from your final bill.`}
          </p>
        </div>
      </div>
      <AdvancePayButton request={request} advance={advance} disabled={disabled} size="sm" className="shrink-0" />
    </div>
  );
}

function PaidAdvance({ advance }: { advance: AdvanceResponse }) {
  // A held receipt has an id but no number, and its PDF isn't served to customers yet.
  const receiptId = advance.receipt_number ? advance.receipt_document_id : null;
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Check className="h-5 w-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Advance paid</p>
          <p className="text-xs text-muted-foreground">
            {formatInr(advance.amount)}
            {advance.receipt_number ? ` · Receipt ${advance.receipt_number}` : ' · Your receipt is being prepared.'}
          </p>
        </div>
      </div>
      {receiptId && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0 gap-1.5"
          // Called straight from the click: viewBillPdf opens its tab before awaiting.
          onClick={() => void viewBillPdf({ id: receiptId, number: advance.receipt_number })}
        >
          <Eye className="h-4 w-4" aria-hidden="true" /> View receipt
        </Button>
      )}
    </div>
  );
}
